// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useKanjiMemoryProgress } from "./useKanjiMemoryProgress";
import { useGrammarProgress } from "./useGrammarProgress";
import { useVocabularyProgress } from "./useVocabularyProgress";
import { useConjugationProgress } from "./useConjugationProgress";
import { useKanjiDrawCounts } from "./useKanjiDrawCounts";
import { useKanaDrawCounts } from "./useKanaDrawCounts";
import { useKanaListenCounts } from "./useKanaListenCounts";
import { useKanjiListenCounts } from "./useKanjiListenCounts";
import { resolveContinueLearning } from "../utils/continueLearning";

// Surfaces a single recommended "continue learning" destination for the
// homepage, computed from progress the app already tracks. Returns
// isReady: false until it's safe to show a recommendation, so the homepage
// never flashes an anonymous-only recommendation that's then replaced once
// an authenticated user's cloud progress arrives.
//
export function useContinueLearning() {
    const { status: authStatus } = useAuth();
    const { progress: kanjiProgress, syncStatus: kanjiSyncStatus } = useKanjiMemoryProgress();
    const { progress: grammarProgress, syncStatus: grammarSyncStatus } = useGrammarProgress();
    const { progress: vocabularyProgress, syncStatus: vocabularySyncStatus } = useVocabularyProgress();
    const { progress: conjugationProgress, syncStatus: conjugationSyncStatus } = useConjugationProgress();
    const { counts: kanjiDrawCounts, syncStatus: kanjiDrawSyncStatus, cloudLoadFailed: kanjiDrawCloudLoadFailed } = useKanjiDrawCounts();
    const { counts: kanaDrawCounts, syncStatus: kanaDrawSyncStatus, cloudLoadFailed: kanaDrawCloudLoadFailed } = useKanaDrawCounts();
    const { counts: kanaListenCounts, syncStatus: kanaListenSyncStatus, cloudLoadFailed: kanaListenCloudLoadFailed } = useKanaListenCounts();
    const { counts: kanjiListenCounts, syncStatus: kanjiListenSyncStatus, cloudLoadFailed: kanjiListenCloudLoadFailed } = useKanjiListenCounts();

    const progressSyncStatuses = [
        kanjiSyncStatus,
        grammarSyncStatus,
        vocabularySyncStatus,
        conjugationSyncStatus,
        kanjiDrawSyncStatus,
        kanaDrawSyncStatus,
        kanaListenSyncStatus,
        kanjiListenSyncStatus,
    ];

    const isReady =
        authStatus === "unauthenticated" ||
        (authStatus === "authenticated" && progressSyncStatuses.every((syncStatus) => syncStatus !== "loading"));

    const recommendation = useMemo(() => {
        if (!isReady) return null;
        try {
            return resolveContinueLearning({
                kanjiProgress,
                grammarProgress,
                vocabularyProgress,
                kanjiDrawCounts,
                kanaDrawCounts,
            });
        } catch {
            // A continue-learning failure should never break the homepage.
            return null;
        }
    }, [isReady, kanjiProgress, grammarProgress, vocabularyProgress, kanjiDrawCounts, kanaDrawCounts]);

    return {
        isReady,
        recommendation,
        kanjiProgress,
        kanaDrawCounts,
        kanjiDrawCounts,
        kanaListenCounts,
        kanjiListenCounts,
        // Exposed so the missions card can tell "nothing drawn/listened to
        // yet" from "the progress couldn't be loaded" (isReady treats "error"
        // as ready).
        kanaDrawSyncStatus,
        kanjiDrawSyncStatus,
        kanaListenSyncStatus,
        kanjiListenSyncStatus,
        kanaDrawCloudLoadFailed,
        kanjiDrawCloudLoadFailed,
        kanaListenCloudLoadFailed,
        kanjiListenCloudLoadFailed,
        grammarProgress,
        vocabularyProgress,
        conjugationProgress,
    };
}
